import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const MODES = ["Bank Transfer", "UPI", "Cheque", "Cash", "Other"];
const fmtCur = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

// Mirror the backend TDS derivation (invoice_type → TDS%).
const tdsRateFor = (invoiceType) => {
  const t = (invoiceType || "").toLowerCase().replace(/\s+/g, "");
  if (t === "gst") return 2;
  if (t === "non-gst" || t === "nongst") return 1;
  return 0;
};

export default function PaymentDialog({ open, onOpenChange, creator, onSaved }) {
  const [form, setForm] = useState({
    amount: "", payment_type: "Partial", paid_at: "", mode: "Bank Transfer",
    reference: "", notes: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const now = new Date();
      now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
      setForm({
        amount: "", payment_type: "Partial",
        paid_at: now.toISOString().slice(0, 16),
        mode: "Bank Transfer", reference: "", notes: "",
      });
    }
  }, [open]);

  const submit = async () => {
    const amt = parseFloat(form.amount || 0);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    if (!creator?.id) { toast.error("No creator selected"); return; }
    setSaving(true);
    try {
      const paidIso = form.paid_at ? new Date(form.paid_at).toISOString() : "";
      const { data } = await api.post(`/creators/${creator.id}/payments`, {
        ...form, amount: amt, paid_at: paidIso,
      });
      toast.success("Payment logged");
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to log");
    } finally { setSaving(false); }
  };

  const finalCost = Number(creator?.final_cost) || 0;
  const totalPaid = Number(creator?.total_paid) || 0;
  const remaining = Math.max(finalCost - totalPaid, 0);
  const invoiceType = creator?.invoice_type || "";
  const tdsPct = tdsRateFor(invoiceType);
  const amt = parseFloat(form.amount || 0) || 0;
  const tdsOnThisPayment = Math.round(amt * tdsPct) / 100;
  const netToCreator = amt - tdsOnThisPayment;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            Log payment · @{creator?.username}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3 text-xs" data-testid="payment-dialog-summary">
          <div className="p-2 rounded-md bg-slate-50 border border-slate-200">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Final cost</div>
            <div className="font-mono font-semibold text-slate-900">{fmtCur(finalCost)}</div>
          </div>
          <div className="p-2 rounded-md bg-emerald-50 border border-emerald-200">
            <div className="text-[10px] uppercase tracking-wider text-emerald-700">Already paid</div>
            <div className="font-mono font-semibold text-emerald-800">{fmtCur(totalPaid)}</div>
          </div>
          <div className="p-2 rounded-md bg-rose-50 border border-rose-200">
            <div className="text-[10px] uppercase tracking-wider text-rose-700">Remaining</div>
            <div className="font-mono font-semibold text-rose-800">{fmtCur(remaining)}</div>
          </div>
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Gross amount (₹) *</Label>
              <Input
                type="number"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder="0"
                className="mt-1"
                data-testid="payment-amount-input"
              />
              <button
                onClick={() => setForm({ ...form, amount: String(remaining), payment_type: "Full" })}
                className="text-[11px] text-blue-600 hover:text-blue-700 mt-1"
                data-testid="pay-full-btn"
              >
                Pay full remaining
              </button>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Type</Label>
              <Select value={form.payment_type} onValueChange={(v) => setForm({ ...form, payment_type: v })}>
                <SelectTrigger className="mt-1" data-testid="payment-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Partial">Partial</SelectItem>
                  <SelectItem value="Full">Full</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Paid on</Label>
              <Input
                type="datetime-local"
                value={form.paid_at}
                onChange={(e) => setForm({ ...form, paid_at: e.target.value })}
                className="mt-1"
                data-testid="payment-date-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Mode</Label>
              <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
                <SelectTrigger className="mt-1" data-testid="payment-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider text-slate-500">Reference (UTR / txn id)</Label>
              <Input
                value={form.reference}
                onChange={(e) => setForm({ ...form, reference: e.target.value })}
                placeholder="e.g. AXIS0123456789"
                className="mt-1"
                data-testid="payment-reference-input"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider text-slate-500">Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                className="mt-1"
                data-testid="payment-notes-input"
              />
            </div>
          </div>

          {amt > 0 && (
            <div className="p-3 rounded-md border border-amber-200 bg-amber-50 text-xs space-y-1" data-testid="tds-breakdown">
              <div className="font-semibold text-amber-800 uppercase tracking-wider text-[10px]">
                Auto TDS · {invoiceType || "no invoice type"}
              </div>
              {tdsPct > 0 ? (
                <>
                  <div className="text-amber-900">
                    TDS of <b>{fmtCur(tdsOnThisPayment)}</b> ({tdsPct}% of gross) will be withheld on this payment.
                  </div>
                  <div className="text-emerald-800 pt-1 border-t border-amber-200">
                    Gross entered: <span className="font-mono font-semibold">{fmtCur(amt)}</span>
                    {" · "}Net paid to creator: <span className="font-mono font-semibold">{fmtCur(netToCreator)}</span>
                  </div>
                </>
              ) : (
                <div className="text-amber-900">
                  Set the creator's invoice type (GST or Non-GST) to enable auto TDS withholding.
                </div>
              )}
              {invoiceType === "GST" && (
                <div className="text-[11px] text-indigo-700 pt-1 border-t border-amber-200">
                  GST reimbursement is tracked separately. Log it in the Accountant View when the creator raises their GST invoice.
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="payment-save-btn"
          >
            {saving ? "Saving…" : "Log payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
